/**
 * Payroll (`/api/admin/v2/hr/payroll`) and the caller's payslips
 * (`/api/admin/v2/workplace/payslips`). `payroll.view` reads, `payroll.manage`
 * prepares, approves (never a run you last calculated) and marks paid.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import type { PayRun, Payslip, StatutoryRateSet } from '@/client/types/hr';
import { HR_BASE, WORKPLACE_BASE, hrKeys, useHrApi } from './people';

const BASE = `${HR_BASE}/payroll`;
const keys = {
  runs: () => [...hrKeys.all, 'payroll', 'runs'] as const,
  run: (id?: string) => [...hrKeys.all, 'payroll', 'run', id] as const,
  rates: () => [...hrKeys.all, 'payroll', 'rates'] as const,
  mine: () => [...hrKeys.workplace(), 'payslips'] as const,
};
const q = { staleTime: 15_000, gcTime: DEFAULT_GC_TIME };

export const usePayRunsQuery = () => {
  const api = useHrApi(BASE);
  return useQuery({ queryKey: keys.runs(), queryFn: async () => (await api<{ runs: PayRun[] }>('/runs')).runs ?? [], ...q });
};

export const usePayRunQuery = (id?: string) => {
  const api = useHrApi(BASE);
  return useQuery({ queryKey: keys.run(id), queryFn: () => api<PayRun>(`/runs/${encodeURIComponent(id as string)}`), enabled: Boolean(id), ...q });
};

export const useRateSetsQuery = () => {
  const api = useHrApi(BASE);
  return useQuery({ queryKey: keys.rates(), queryFn: async () => (await api<{ rates: StatutoryRateSet[] }>('/rates')).rates ?? [], ...q });
};

export const useMyPayslipsQuery = () => {
  const api = useHrApi(WORKPLACE_BASE);
  return useQuery({ queryKey: keys.mine(), queryFn: async () => (await api<{ payslips: Payslip[] }>('/payslips')).payslips ?? [], ...q });
};

function useMut<TIn, TOut>(fn: (api: ReturnType<typeof useHrApi>, input: TIn) => Promise<TOut>) {
  const api = useHrApi(BASE);
  const qc = useQueryClient();
  return useMutation({ mutationFn: (input: TIn) => fn(api, input), onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }) });
}

export const useCreatePayRunMutation = () =>
  useMut((api, body: { period: string; payDate?: string | null; notes?: string }) => api<PayRun>('/runs', 'POST', body));
export const useCalculatePayRunMutation = () => useMut((api, id: string) => api<PayRun>(`/runs/${encodeURIComponent(id)}/calculate`, 'POST'));
export const usePayRunActionMutation = () =>
  useMut((api, { id, action }: { id: string; action: 'approve' | 'pay' | 'delete' }) =>
    api<PayRun>(`/runs/${encodeURIComponent(id)}/${action}`, 'POST'),
  );
export const useAddRateSetMutation = () =>
  useMut((api, body: Omit<StatutoryRateSet, 'id'>) => api<StatutoryRateSet>('/rates', 'POST', body));
